import { AlertTriangle, ChevronDown } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { resolveWarningText } from "./warningMessages";
import type { WarningEntry } from "@/types/api";

/**
 * Avisos da execução, compartilhado entre o `AgentPageLayout` e o workbench do
 * Functional. Morava dentro do layout, e o workbench, que não o usa, ficava sem
 * banner nenhum (#816). Lista vazia não desenha nada.
 *
 * um aviso pode chegar estruturado ({code, params, message})
 * `resolveWarningText` traduz os 5 códigos do ambiente de demo; qualquer
 * outro (string solta, ou `code` ainda não mapeado) renderiza `message` cru,
 * exatamente como antes desta fase.
 *
 * Numa run real do Functional foram 18 avisos longos, colados um no outro e
 * empurrando o pipeline para baixo da dobra. Por isso o quadro começa fechado,
 * numa linha com a quantidade, e abre no clique; cada aviso tem divisória, e o
 * texto repetido aparece uma vez só — a execução acumula o aviso de cada
 * passada pelos nós de BOM, e três deles chegavam triplicados.
 */
export function WarningsBanner({
  warnings,
  className,
}: {
  warnings: WarningEntry[];
  className?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (warnings.length === 0) return null;
  const texts = [
    ...new Set(warnings.map((w) => (typeof w === "string" ? w : resolveWarningText(t, w)))),
  ];
  return (
    <div className={cn("rounded-lg border border-amber-200 bg-amber-50 p-3", className)}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-2 text-left text-sm"
      >
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
        <span className="font-medium text-amber-800">{t("Warnings")}</span>
        <span
          data-testid="warnings-count"
          className="rounded-full bg-amber-100 px-1.5 text-[11px] font-semibold tabular-nums text-amber-800"
        >
          {texts.length}
        </span>
        <ChevronDown
          aria-hidden
          className={cn("ml-auto h-4 w-4 text-amber-700 transition-transform", !open && "-rotate-90")}
        />
      </button>
      {open && (
        <ul className="mt-2 divide-y divide-amber-200 pl-6 text-sm text-amber-700">
          {texts.map((text) => (
            <li key={text} className="py-1.5 first:pt-0 last:pb-0">
              {text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
